import { useScrollReveal } from '../../../hooks/useScrollReveal';

const principles = [
  {
    number: '01',
    title: 'Clarity',
    description: 'Every spatial decision is made with purpose — clean lines, intentional form, nothing extraneous.',
  },
  {
    number: '02',
    title: 'Livability',
    description: 'Architecture designed around how you actually live — natural light, flow, and function at the core.',
  },
  {
    number: '03',
    title: 'Precision',
    description: 'Detail-focused execution at every scale — from structural decisions to the selection of final materials.',
  },
];

export default function Philosophy() {
  const headerRef = useScrollReveal();
  const gridRef = useScrollReveal();

  return (
    <section className="relative py-36 bg-dark-ink overflow-hidden">
      {/* Subtle background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.08]"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/febdaf131cec63e928aa1b59efd467bc.jpg)',
        }}
      />

      {/* Large decorative text */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[220px] lg:text-[320px] font-light text-white/[0.02] select-none pointer-events-none whitespace-nowrap leading-none">
        Talo
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll text-center mb-20">
          <p className="font-sans text-[10px] tracking-[0.24em] uppercase text-bronze mb-6">Our Approach</p>
          <h2 className="font-serif text-5xl lg:text-7xl font-light text-white leading-[1.05] max-w-3xl mx-auto">
            Design is not decoration.<br />
            <em className="not-italic text-white/45">It is the clarity of how a life is organized.</em>
          </h2>
        </div>

        {/* Principles grid */}
        <div ref={gridRef} className="reveal-on-scroll reveal-delay-2 grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-white/10 pt-16">
          {principles.map((p, i) => (
            <PrincipleItem key={i} principle={p} last={i === principles.length - 1} />
          ))}
        </div>

        {/* Bottom strip */}
        <div className="mt-20 pt-16 border-t border-white/10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <p className="font-sans text-sm text-white/35 max-w-lg leading-relaxed">
            Since 2002, Talo Architect has been designing residential architecture across New York City and Westchester County — guided by these three principles on every project.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-building-line text-bronze text-sm" />
              </div>
              <span className="font-sans text-[11px] text-white/35 tracking-wide whitespace-nowrap">Residential Architecture</span>
            </div>
            <div className="w-px h-6 bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-bronze text-sm" />
              </div>
              <span className="font-sans text-[11px] text-white/35 tracking-wide whitespace-nowrap">NYC &amp; Westchester</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrincipleItem({
  principle,
  last,
}: {
  principle: (typeof principles)[0];
  last: boolean;
}) {
  const ref = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`reveal-on-scroll group px-0 lg:px-10 py-10 lg:py-0 first:pl-0 ${!last ? 'border-b lg:border-b-0 lg:border-r border-white/10' : ''}`}
    >
      <div className="flex items-start gap-5">
        <span className="font-serif text-5xl font-light text-white/12 group-hover:text-bronze/25 transition-colors duration-500 flex-shrink-0 leading-none">
          {principle.number}
        </span>
        <div>
          <h4 className="font-serif text-2xl font-light text-white mb-3 group-hover:text-bronze/80 transition-colors duration-300">
            <a href="#principles" className="cursor-default">{principle.title}</a>
          </h4>
          <p className="font-sans text-sm text-white/40 leading-relaxed group-hover:text-white/60 transition-colors duration-300">
            {principle.description}
          </p>
        </div>
      </div>
    </div>
  );
}
